// Traductions du mode Lite : page « pfm » (Prop Firm Manager).
export const fr = {
  title: "Prop Firm Manager",
  subtitle: "Ce que tes prop firms te coûtent, ce qu'elles te rapportent.",
  actions: {
    import: "Importer",
    expense: "Dépense",
    payout: "Payout",
  },
  empty: {
    title: "Suis ce que tes prop firms te coûtent et te rapportent",
    text: "Ajoute tes comptes prop : Edgely suit l'objectif et le drawdown de chacun, tes dépenses (challenges, activations, abonnements) et tes payouts, pour te donner ton vrai ROI.",
    addAccount: "Ajouter un compte prop",
    connect: "Connecter Tradovate",
  },
  kpi: {
    payouts: "Payouts reçus",
    payoutsHint: (n: number, period: string) => `${n} payout${n > 1 ? "s" : ""} · ${period}`,
    expenses: "Dépenses",
    expensesHint: "Challenges, activations et charges",
    net: "Profit net",
    netHint: "Payouts moins dépenses",
    roi: "ROI",
    roiHint: "Sur la période",
    active: "Comptes actifs",
    activeHint: (challenges: number, funded: number) => `${challenges} challenge${challenges > 1 ? "s" : ""} · ${funded} funded`,
    passRate: "Challenges réussis",
    passRateHint: (passed: number, done: number) => `${passed} sur ${done} terminé${done > 1 ? "s" : ""}`,
    passRateNone: "Aucun challenge terminé",
  },
  cashflow: {
    title: "Payouts et dépenses par mois",
    subtitle: "6 derniers mois, avec le profit net cumulé",
  },
  recap: {
    title: "Récap du mois",
    challenges: "Challenges",
    activations: "Activations",
    recurring: "Charges récurrentes",
    oneOff: "Frais ponctuels",
    payouts: "Payouts",
    totalExpenses: "Total des dépenses",
    net: "Net du mois",
    roi: "ROI du mois",
  },
  calendar: {
    title: "Calendrier",
    subtitle: "Payouts, dépenses et changements de statut",
    dayDetail: (date: string) => `Le ${date}`,
    legendPayout: "Payout",
    legendExpense: "Dépense",
    legendRecurring: "Charge récurrente",
    legendFunded: "Passage en funded",
    legendClosed: "Compte cramé ou clôturé",
  },
  events: {
    payout: (name: string) => `Payout · ${name}`,
    challenge: (name: string) => `Challenge · ${name}`,
    activation: (name: string) => `Activation · ${name}`,
    recurring: (name: string) => name,
    oneOff: (name: string) => name,
    funded: (name: string) => `${name} passe en funded`,
    reset: (name: string) => `${name} remis à zéro`,
    breached: (name: string) => `${name} cramé`,
    passed: (name: string) => `${name} validé`,
    closed: (name: string) => `${name} clôturé`,
  },
  upcoming: {
    title: "À venir",
    subtitle: "Prélèvements des 30 prochains jours",
    none: "Aucun prélèvement prévu.",
    global: "Charge globale",
    toConfirmTitle: "À confirmer",
    toConfirmHint: "Ces prélèvements sont passés : ont-ils bien eu lieu ?",
    paid: "Prélevé",
    notPaid: "Pas prélevé",
    otherAmount: "Autre montant",
    actions: "Actions",
    editOne: "Modifier ce prélèvement",
    editFrom: "Modifier à partir d'ici",
    skip: "Pas prélevé ce mois-ci",
    restore: "Rétablir ce prélèvement",
    cancelled: "J'ai résilié",
    keep: "Je garde",
    modified: "Modifié",
    skipped: "Pas prélevé",
    cancelQuestion: (name: string) => `${name} est clôturé : as-tu résilié son abonnement ? Sinon la prop firm prélèvera au renouvellement.`,
    cancelHint: "Sans résiliation, la prop firm prélève au renouvellement et remet le compte à zéro.",
    editOneTitle: "Montant de ce prélèvement",
    editFromTitle: "Nouveau montant à partir de ce prélèvement",
    editFromHint: "Les prélèvements passés gardent leur montant.",
    amount: "Montant",
    save: "Enregistrer",
    cancel: "Annuler",
    error: "Action impossible, réessaie.",
  },
  activity: {
    title: "Activité récente",
    none: "Rien pour l'instant.",
  },
  firms: {
    title: "Par prop firm",
    subtitle: "Depuis le début",
    firm: "Prop firm",
    accounts: "Comptes",
    accountsValue: (active: number, total: number) => `${active} actif${active > 1 ? "s" : ""} / ${total}`,
    passRate: "Réussite",
    spent: "Dépensé",
    payouts: "Payouts",
    net: "Net",
    roi: "ROI",
    globalNote: (amount: string) => `Charges globales non rattachées à une prop firm : ${amount}.`,
  },
  byPeriod: {
    title: "Suivi par période",
    subtitle: "Payouts et dépenses, période par période",
    month: "Mois",
    quarter: "Trimestre",
    year: "Année",
    quarterLabel: (q: number, year: number) => `T${q} ${year}`,
    period: "Période",
    payouts: "Payouts",
    challenges: "Challenges",
    activations: "Activations",
    charges: "Charges",
    totalExpenses: "Total dépenses",
    net: "Net",
    roi: "ROI",
    total: "Total",
    none: "Aucun mouvement pour ces filtres",
  },
  tracking: {
    title: "Suivi par compte",
    subtitle: "Clique sur un compte pour le détail",
    showBreached: (n: number) => `Afficher les comptes cramés ou inactifs (${n})`,
    account: "Compte",
    status: "Statut",
    balance: "Solde",
    target: "Objectif",
    cushion: "Marge drawdown",
    spent: "Dépensé",
    payouts: "Payouts",
    net: "Net",
    none: "Aucun compte pour ces filtres",
  },
  wallet: {
    subtitle: "Tes payouts TPT arrivent ici avant retrait",
    available: "Solde disponible",
    initial: "Solde initial",
    transfers: "Transferts reçus",
    withdrawn: "Retraits",
    add: "Ajouter",
    transfer: (name: string) => `Transfert · ${name}`,
    withdrawal: "Retrait",
    none: "Aucun mouvement pour l'instant.",
    recent: "Récents",
  },
  byType: {
    title: "Dépenses par type",
    subtitle: "Depuis le début",
    none: "Aucune dépense enregistrée.",
  },
  expenseDialog: {
    title: "Ajouter une dépense",
    description: "Une dépense liée à un compte (prix du challenge, frais d'activation, abonnement) se range sur ce compte. Sinon, crée une charge globale.",
    scope: "Rattacher à",
    global: "Aucun compte (charge globale)",
    openAccount: "Gérer les coûts de ce compte",
  },
};

export const en: typeof fr = {
  title: "Prop Firm Manager",
  subtitle: "What your prop firms cost you, and what they pay you.",
  actions: {
    import: "Import",
    expense: "Expense",
    payout: "Payout",
  },
  empty: {
    title: "Track what your prop firms cost and pay you",
    text: "Add your prop accounts: Edgely tracks each one's target and drawdown, your expenses (challenges, activations, subscriptions) and your payouts, to give you your real ROI.",
    addAccount: "Add a prop account",
    connect: "Connect Tradovate",
  },
  kpi: {
    payouts: "Payouts received",
    payoutsHint: (n: number, period: string) => `${n} payout${n > 1 ? "s" : ""} · ${period}`,
    expenses: "Expenses",
    expensesHint: "Challenges, activations and charges",
    net: "Net profit",
    netHint: "Payouts minus expenses",
    roi: "ROI",
    roiHint: "Over the period",
    active: "Active accounts",
    activeHint: (challenges: number, funded: number) => `${challenges} challenge${challenges > 1 ? "s" : ""} · ${funded} funded`,
    passRate: "Challenges passed",
    passRateHint: (passed: number, done: number) => `${passed} of ${done} finished`,
    passRateNone: "No finished challenge",
  },
  cashflow: {
    title: "Payouts and expenses by month",
    subtitle: "Last 6 months, with cumulative net profit",
  },
  recap: {
    title: "Monthly recap",
    challenges: "Challenges",
    activations: "Activations",
    recurring: "Recurring charges",
    oneOff: "One-off fees",
    payouts: "Payouts",
    totalExpenses: "Total expenses",
    net: "Net for the month",
    roi: "ROI for the month",
  },
  calendar: {
    title: "Calendar",
    subtitle: "Payouts, expenses and status changes",
    dayDetail: (date: string) => date,
    legendPayout: "Payout",
    legendExpense: "Expense",
    legendRecurring: "Recurring charge",
    legendFunded: "Moved to funded",
    legendClosed: "Account breached or closed",
  },
  events: {
    payout: (name: string) => `Payout · ${name}`,
    challenge: (name: string) => `Challenge · ${name}`,
    activation: (name: string) => `Activation · ${name}`,
    recurring: (name: string) => name,
    oneOff: (name: string) => name,
    funded: (name: string) => `${name} moved to funded`,
    reset: (name: string) => `${name} reset`,
    breached: (name: string) => `${name} breached`,
    passed: (name: string) => `${name} passed`,
    closed: (name: string) => `${name} closed`,
  },
  upcoming: {
    title: "Upcoming",
    subtitle: "Charges due in the next 30 days",
    none: "No charge scheduled.",
    global: "Global charge",
    toConfirmTitle: "To confirm",
    toConfirmHint: "These charges are past due: were you actually billed?",
    paid: "Billed",
    notPaid: "Not billed",
    otherAmount: "Different amount",
    actions: "Actions",
    editOne: "Edit this charge",
    editFrom: "Edit from this one on",
    skip: "Not billed this month",
    restore: "Restore this charge",
    cancelled: "I cancelled",
    keep: "I'm keeping it",
    modified: "Edited",
    skipped: "Not billed",
    cancelQuestion: (name: string) => `${name} is closed: did you cancel its subscription? If not, the prop firm will bill you on renewal.`,
    cancelHint: "If not, the prop firm bills you on renewal and resets the account.",
    editOneTitle: "Amount of this charge",
    editFromTitle: "New amount from this charge on",
    editFromHint: "Past charges keep their amount.",
    amount: "Amount",
    save: "Save",
    cancel: "Cancel",
    error: "Something went wrong, try again.",
  },
  activity: {
    title: "Recent activity",
    none: "Nothing yet.",
  },
  firms: {
    title: "By prop firm",
    subtitle: "All time",
    firm: "Prop firm",
    accounts: "Accounts",
    accountsValue: (active: number, total: number) => `${active} active / ${total}`,
    passRate: "Pass rate",
    spent: "Spent",
    payouts: "Payouts",
    net: "Net",
    roi: "ROI",
    globalNote: (amount: string) => `Global charges not tied to a prop firm: ${amount}.`,
  },
  byPeriod: {
    title: "Tracking by period",
    subtitle: "Payouts and expenses, period by period",
    month: "Month",
    quarter: "Quarter",
    year: "Year",
    quarterLabel: (q: number, year: number) => `Q${q} ${year}`,
    period: "Period",
    payouts: "Payouts",
    challenges: "Challenges",
    activations: "Activations",
    charges: "Charges",
    totalExpenses: "Total expenses",
    net: "Net",
    roi: "ROI",
    total: "Total",
    none: "No activity for these filters",
  },
  tracking: {
    title: "Per-account tracking",
    subtitle: "Click an account for details",
    showBreached: (n: number) => `Show breached or inactive accounts (${n})`,
    account: "Account",
    status: "Status",
    balance: "Balance",
    target: "Target",
    cushion: "Drawdown room",
    spent: "Spent",
    payouts: "Payouts",
    net: "Net",
    none: "No account for these filters",
  },
  wallet: {
    subtitle: "Your TPT payouts land here before withdrawal",
    available: "Available balance",
    initial: "Initial balance",
    transfers: "Transfers received",
    withdrawn: "Withdrawals",
    add: "Add",
    transfer: (name: string) => `Transfer · ${name}`,
    withdrawal: "Withdrawal",
    none: "No movement yet.",
    recent: "Recent",
  },
  byType: {
    title: "Expenses by type",
    subtitle: "All time",
    none: "No expense recorded.",
  },
  expenseDialog: {
    title: "Add an expense",
    description: "An expense tied to an account (challenge price, activation fee, subscription) belongs on that account. Otherwise, create a global charge.",
    scope: "Attach to",
    global: "No account (global charge)",
    openAccount: "Manage this account's costs",
  },
};
